import { GoPlus } from 'react-icons/go';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { RxCross2 } from 'react-icons/rx';

import { useEffect, useState } from 'react';
import { ViewableResponse } from '../types/global';
import { AutocompleteDialog } from './autocomplete/AutocompleteDialog';
import Fuse, { FuseResult, FuseResultMatch } from 'fuse.js';
import { RootState } from '../types/graph';
import { useSelector } from 'react-redux';
import { compact } from 'lodash';
import { removeStopwords, fra } from 'stopword';

type ResponseAutocompleteProps = {
  questionLabel?: string;
  questionDescription?: string;
  placeholder?: string;
  multipleChoice?: boolean;
  hideResponsesByDefault?: boolean;
  responses: ViewableResponse[];
  questionExtId: string;
  onChange: (response: ViewableResponse, value: boolean | string) => void;
};

type SelectedResponse = ViewableResponse & { otherValue?: string };

export const ResponseAutocomplete = ({
  multipleChoice,
  hideResponsesByDefault,
  questionLabel,
  questionDescription,
  placeholder,
  questionExtId,
  responses,
  onChange,
}: ResponseAutocompleteProps) => {
  const CUSTOM_STOPWORDS = [
    `j'ai`,
    'jai',
    `j'étais`,
    'fais',
    'fait',
    'sens',
    'sent',
    `c'est`,
    `c'étais`,
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState<string>('');
  const [fuseMatches, setFuseMatches] = useState<FuseResultMatch[]>([]);
  const [otherResponses, setOtherResponses] = useState<string[]>([]);
  const [filteredResponses, setFilteredResponses] = useState<
    ViewableResponse[]
  >([]);

  const userResponses = useSelector((state: RootState) => state.userResponses);

  const isResponseSelected = (response: ViewableResponse) =>
    !!userResponses[questionExtId]?.[response.extId];

  const closeDialog = () => {
    setIsOpen(false);
    setSearchText('');
  };

  const otherResponse = responses.find(
    (r) => r.format === 'AUTOCOMPLETE_OTHER',
  );

  useEffect(() => {
    if (!otherResponse) {
      setOtherResponses((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const savedValue = userResponses[questionExtId]?.[otherResponse.extId];
    if (savedValue) {
      setOtherResponses(
        savedValue
          .toString()
          .split(', ')
          .map((r) => r.trim()),
      );
    } else {
      setOtherResponses((prev) => (prev.length === 0 ? prev : []));
    }
  }, [otherResponse, questionExtId, userResponses]);

  useEffect(() => {
    if (searchText.length < 3) {
      setFilteredResponses(
        responses.filter(
          (response) => response.format !== 'AUTOCOMPLETE_OTHER',
        ),
      );
      setFuseMatches([]);
      return;
    }

    const words = searchText.replace(`'`, ' ').toLowerCase().split(' ');
    const filteredWords = removeStopwords(words, [
      ...fra,
      ...CUSTOM_STOPWORDS,
    ]).filter((w) => w.length > 2);

    // Perform search for each word
    const searchResults = filteredWords
      .flatMap((searchstring) => {
        const fuse = new Fuse(responses, {
          keys: ['label', 'keywords'],
          threshold: 0.3,
          ignoreDiacritics: true,
          includeScore: true,
          ignoreLocation: true,
          includeMatches: true,
          minMatchCharLength: Math.max(searchstring.length - 1, 3),
          findAllMatches: true,
          shouldSort: true,
          useExtendedSearch: true,
        });

        const f = fuse.search(searchstring);
        return f;
      })
      .reduce<Record<string, FuseResult<ViewableResponse>[]>>((acc, r) => {
        acc[r.item.label] = acc[r.item.label] || [];
        acc[r.item.label].push(r);
        return acc;
      }, {});

    // Aggregate and sort results
    const sortedResults = Object.values(searchResults)
      .map((results) => {
        const mergedResult = results.reduce<FuseResult<ViewableResponse>>(
          (acc, r) => ({
            ...acc,
            matches: [
              {
                ...acc.matches![0],
                indices: [
                  ...(acc.matches?.[0].indices || []),
                  ...(r.matches?.[0].indices || []),
                ],
              },
            ],
            score: (acc.score || 0) + (r.score || 0),
          }),
          results[0],
        );
        return {
          result: mergedResult,
          score:
            results.length +
            (1 - results.reduce((sum, r) => sum + (r.score || 0), 0)),
        };
      })
      .sort((a, b) => b.score - a.score)
      .map(({ result }) => result);

    // Enhance matches for highlighting
    const enhancedResults = sortedResults.map((result) => {
      const match = result.matches?.[0];
      if (match) {
        compact(searchText.toLowerCase().split(' ')).forEach((word) => {
          const matches = [
            ...(match.value || '')
              .toLowerCase()
              .matchAll(new RegExp(word, 'gi')),
          ];
          matches.forEach((m) => {
            if (m?.index) {
              result.matches![0].indices = [
                ...result.matches![0].indices,
                [m.index, m.index + word.length - 1],
              ];
            }
          });
        });
      }
      return result;
    });

    // Update state with results
    setFuseMatches(compact(enhancedResults.map((r) => r.matches?.[0])));
    setFilteredResponses([
      ...enhancedResults
        .map((r) => r.item)
        .filter((r) => r.format !== 'AUTOCOMPLETE_OTHER'),
      ...responses.filter((r) => r.format === 'AUTOCOMPLETE_OTHER'),
    ]);
  }, [searchText, responses]);

  const highlightText = (text: string, highlight: string) => {
    if (!highlight.trim()) return text;

    const match = fuseMatches.find((match) => match.value === text);
    if (!match) return text;

    // Merge overlapping indices for case where match.indices is an array of overlapping ranges e.g "mala" and "ladie" for the word "maladie"
    const mergedIndices = Array.from(match.indices)
      .sort((a: number[], b: number[]) => a[0] - b[0])
      .reduce((acc, [start, end]) => {
        const lastIndex = acc[acc.length - 1];
        if (lastIndex && start <= lastIndex[1] + 1) {
          // If current range overlaps or is adjacent to the last range, merge them
          lastIndex[1] = Math.max(lastIndex[1], end);
        } else {
          // Otherwise add as a new range
          acc.push([start, end]);
        }
        return acc;
      }, [] as number[][]);

    const result = [];
    let lastIndex = 0;

    mergedIndices.forEach(([start, end], index) => {
      if (start > lastIndex) {
        result.push(text.slice(lastIndex, start));
      }

      result.push(
        <span key={`highlight-${index}`} className="text-blue-500">
          {text.slice(start, end + 1)}
        </span>,
      );

      lastIndex = end + 1;
    });

    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }

    return result;
  };

  const handleResponseClick = (response: ViewableResponse) => {
    // Single-choice questions (or an exclusive response) keep only one answer selected.
    const isSingleChoice = !multipleChoice || response.exclusive;

    if (response.format === 'AUTOCOMPLETE_OTHER') {
      // Replace the free-text value for single-choice, append it for multi-choice.
      const newResponses = isSingleChoice
        ? [searchText.trim()]
        : [...otherResponses, searchText.trim()];
      setOtherResponses(newResponses);
      onChange(response, newResponses.join(', '));
    } else {
      onChange(response, !isResponseSelected(response));
    }
    if (isSingleChoice) {
      // Selecting a predefined option clears any previously entered free-text answer.
      // When the selected option IS the free-text ("Autre") response, keep the value
      // just recorded above instead of wiping it (which would block progression).
      if (response.format !== 'AUTOCOMPLETE_OTHER') {
        setOtherResponses([]);
        if (otherResponse) {
          onChange(otherResponse, '');
        }
      }
    }
    closeDialog();
  };

  const handleDeleteResponse = (response: SelectedResponse) => {
    setIsOpen(false);
    if (response.format === 'AUTOCOMPLETE_OTHER' && response.otherValue) {
      const newResponses = otherResponses.filter(
        (r) => r !== response.otherValue,
      );
      setOtherResponses(newResponses);
      const newValue = newResponses.join(', ');
      onChange(response, newValue);
    } else {
      onChange(response, false);
    }
  };

  const selectedResponses = [
    ...responses.filter(
      (r) => isResponseSelected(r) && r.format !== 'AUTOCOMPLETE_OTHER',
    ),
    ...otherResponses.flatMap((response, index) => {
      if (!otherResponse) return [];
      return {
        ...otherResponse,
        id: `other_${index}`,
        label: `${otherResponse.label} `,
        otherValue: response,
      };
    }),
  ];

  return (
    <>
      <div className="flex flex-col items-start justify-center w-full">
        <Button
          variant="autocomplete"
          size="autocomplete"
          onClick={() => setIsOpen(true)}
          className={cn(
            !multipleChoice && selectedResponses.length > 0 ? 'hidden' : '',
          )}
        >
          <span>
            {placeholder ??
              `Entrez ${multipleChoice ? 'vos réponses' : 'votre réponse'}`}
          </span>
          <GoPlus strokeWidth={0.2} />
        </Button>
        <div
          className={`flex flex-row items-center justify-start flex-wrap gap-2 max-w-full ${
            !multipleChoice ? 'w-full' : 'py-2'
          }`}
        >
          {selectedResponses.map((response: SelectedResponse) => (
            <div
              className={cn(
                'rounded-lg flex flex-row bg-blue-200 items-center pl-4 text-base w-fit max-w-full',
                !multipleChoice
                  ? 'w-full justify-between items-center h-12'
                  : 'h-11',
              )}
              key={response.id}
              onClick={() => (!multipleChoice ? setIsOpen(true) : {})}
            >
              <p className="mr-2 truncate font-semibold">
                {response.format === 'AUTOCOMPLETE_OTHER' ? (
                  <>
                    {response.label}
                    <span className="italic opacity-60">
                      ({response.otherValue})
                    </span>
                  </>
                ) : (
                  response.label
                )}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteResponse(response);
                }}
                className="pr-4 py-3 pl-1"
              >
                <RxCross2
                  size={multipleChoice ? 14 : 22}
                  strokeWidth={multipleChoice ? 0.8 : 0.3}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      <AutocompleteDialog
        open={isOpen}
        onClose={closeDialog}
        questionLabel={questionLabel}
        questionDescription={questionDescription}
        searchText={searchText}
        onSearchTextChange={setSearchText}
        searchPlaceholder={placeholder}
        hideResults={hideResponsesByDefault && searchText.length < 3}
      >
        <div className="py-6">
          <span className="font-inter text-sm text-[#B0B0B0]">Suggestions</span>
        </div>
        <div className="flex flex-col gap-6 px-4 pb-4">
          {filteredResponses.map(
            (response: ViewableResponse) =>
              (!isResponseSelected(response) ||
                response.format === 'AUTOCOMPLETE_OTHER') && (
                <div
                  key={response.id}
                  onClick={() => handleResponseClick(response)}
                  className="cursor-pointer"
                >
                  {response.format === 'AUTOCOMPLETE_OTHER' ? (
                    <>
                      {response.label}{' '}
                      <span className="italic opacity-60">
                        ({searchText.trim()})
                      </span>
                    </>
                  ) : (
                    highlightText(response.label, searchText)
                  )}
                </div>
              ),
          )}
        </div>
      </AutocompleteDialog>
    </>
  );
};
